/**
 * React Query hooks for the Faire-Sage Cockpit tool.
 *
 * - useFaireRuns       GET {API_BASE}/v1/commerce/runs (poll every 15s)
 * - useFaireErrors     GET {API_BASE}/v1/commerce/errors
 * - useFaireTrigger    POST {API_BASE}/v1/commerce/trigger (admin only)
 *
 * All requests carry `credentials: 'include'` via the shared apiFetch helper.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { timeAgo } from '@ampersandbrands/ui';
import { apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';

export const FAIRE_API_BASE = `${API_BASE}/v1/commerce`;

export type FaireRunStatus = 'running' | 'complete' | 'partial' | 'failed';

export type FaireRun = {
  run_id: string;
  started_at: string;
  finished_at: string | null;
  trigger_source: string;
  source_channel: string;
  orders_pulled: number | null;
  orders_in_pipefile: number | null;
  orders_written: number | null;
  orders_skipped: number | null;
  orders_failed: number | null;
  s3_pipefile_key: string | null;
  status: FaireRunStatus;
  error_summary: string | null;
};

export type FaireRunsResponse = {
  runs: FaireRun[];
  total_count: number;
  limit: number;
  offset: number;
};

export type FaireErrorStage =
  | 'faire_fetch'
  | 'pipefile_emit'
  | 'laptop_pull'
  | 'viwi33_run'
  | 'tracking_pushback'
  | 'other';

export type FaireErrorActionKind =
  | 'map_customer'
  | 'pick_customer'
  | 'confirm_case_pack'
  | 'reauth_faire'
  | 'view_in_faire'
  | 'wait_for_it'
  | 'unknown';

export type FaireErrorContext = {
  retailer_id: string | null;
  retailer_name: string | null;
  candidates: Array<{ sage_customer_no: string; score: number }>;
  sku: string | null;
};

export type FaireError = {
  error_id: number;
  run_id: string | null;
  occurred_at: string;
  stage: FaireErrorStage;
  faire_order_id: string | null;
  sage_customer_no: string | null;
  error_code: string | null;
  error_message: string | null;
  resolved: number;
  resolved_at: string | null;
  resolved_by: string | null;
  // Server-side translation of the raw error_message into Inside-Sales-
  // readable text. friendly_title is a short headline; action_hint is one
  // sentence explaining what to do; action_kind labels the type of action
  // so the UI can render a matching button.
  friendly_title: string;
  action_hint: string;
  action_kind: FaireErrorActionKind;
  // Structured fields parsed from the raw error_message. Drives the modals.
  context: FaireErrorContext;
};

export type FaireErrorsResponse = {
  errors: FaireError[];
  limit: number;
  offset: number;
};

export type FaireTriggerResponse = {
  accepted: boolean;
  status_url: string;
  note: string;
};

export type UseFaireRunsResult = {
  runs: FaireRun[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<FaireRunsResponse, Error>;
};

/**
 * Fetch recent runs from the runs API. Auto-refetches every 15 seconds while
 * the tab is visible. apiFetch already sets `credentials: 'include'`.
 */
export function useFaireRuns(limit = 20, offset = 0): UseFaireRunsResult {
  const query = useQuery<FaireRunsResponse, Error>({
    queryKey: ['faire-runs', limit, offset],
    queryFn: () => apiGet<FaireRunsResponse>(`${FAIRE_API_BASE}/runs?limit=${limit}&offset=${offset}`),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
  return {
    runs: query.data?.runs ?? [],
    totalCount: query.data?.total_count ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

export type FaireErrorsFilters = {
  stage?: FaireErrorStage;
  resolved?: 0 | 1;
  limit?: number;
  offset?: number;
};

function buildErrorsQuery(filters: FaireErrorsFilters): string {
  const params = new URLSearchParams();
  if (filters.stage) params.set('stage', filters.stage);
  if (filters.resolved === 0 || filters.resolved === 1) {
    params.set('resolved', String(filters.resolved));
  }
  params.set('limit', String(filters.limit ?? 100));
  params.set('offset', String(filters.offset ?? 0));
  return params.toString();
}

export type UseFaireErrorsResult = {
  errors: FaireError[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<FaireErrorsResponse, Error>;
};

export function useFaireErrors(filters: FaireErrorsFilters = {}): UseFaireErrorsResult {
  const qs = buildErrorsQuery(filters);
  const query = useQuery<FaireErrorsResponse, Error>({
    queryKey: ['faire-errors', filters],
    queryFn: () => apiGet<FaireErrorsResponse>(`${FAIRE_API_BASE}/errors?${qs}`),
    staleTime: 15_000,
  });
  return {
    errors: query.data?.errors ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

/**
 * Count of customer-creation-pending errors. Powers the "Customers awaiting
 * approval" KPI tile. Filters server-side by stage=other + resolved=0, then
 * narrows client-side to rows whose message mentions manual customer creation.
 */
export type UseFaireCustomerApprovalCountResult = {
  count: number;
  isLoading: boolean;
  error: Error | null;
};

export function useFaireCustomerApprovalCount(): UseFaireCustomerApprovalCountResult {
  const query = useQuery<FaireErrorsResponse, Error>({
    queryKey: ['faire-errors', 'customer-approval-count'],
    queryFn: () =>
      apiGet<FaireErrorsResponse>(`${FAIRE_API_BASE}/errors?stage=other&resolved=0&limit=500`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 20_000,
  });
  const rows = query.data?.errors ?? [];
  const count = rows.filter((r) =>
    (r.error_message ?? '').toLowerCase().includes('manual customer creation required'),
  ).length;
  return {
    count,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Mutation that POSTs to /trigger. On success, invalidates the runs query so
 * the dashboard refetches and the freshly-started run shows up.
 */
export function useFaireTrigger() {
  const qc = useQueryClient();
  return useMutation<FaireTriggerResponse, Error, void>({
    mutationFn: () => apiPost<FaireTriggerResponse>(`${FAIRE_API_BASE}/trigger`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['faire-runs'] });
    },
  });
}

/**
 * Group OIDs from spec Task 7 (state file `resolved_values`).
 *
 *   cockpit-faire-sage-viewer  50951245-c3e5-4016-b0de-d38a71522175
 *   cockpit-faire-sage-admin   afc258e5-d1ac-4b31-af4b-bb2a775ebe8b
 *
 * The session JWT carries Entra group object IDs (per the Cockpit authorizer
 * contract), so the UI matches against OIDs, not displayNames.
 */
export const FAIRE_VIEWER_GROUP_OID = '50951245-c3e5-4016-b0de-d38a71522175';
export const FAIRE_ADMIN_GROUP_OID = 'afc258e5-d1ac-4b31-af4b-bb2a775ebe8b';

// These now receive the principal's accessSlug strings (aliased through
// user.groups in useSession), not Entra group OIDs. The FAIRE_*_GROUP_OID
// constants above are retained for reference but no longer used for gating.
//
// Binary access-grant model (spec 00_access-binary-grants.md, 2026-08-09):
// these Faire widgets live on the Orders tool (Faire folds into Commerce >
// Orders), so both collapse to the single commerce.orders accessSlug, no more
// viewer/admin distinction. Replaces the old commerce:read / :write / :admin
// trio.
export function hasFaireViewer(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('commerce.orders');
}

export function hasFaireAdmin(perms: string[] | undefined, superAdmin = false): boolean {
  return hasFaireViewer(perms, superAdmin);
}

/**
 * Human-friendly relative time. Returns "just now", "5 minutes ago",
 * "2 hours ago", "3 days ago". Past 14 days it falls back to a plain date.
 * Delegates to the shared `timeAgo` so every Cockpit surface phrases relative
 * time the same way; kept as an export because pages import it from here.
 */
export function relativeTime(iso: string): string {
  return timeAgo(iso) || iso;
}

/**
 * UTC start of today as an ISO string. Used to bucket "orders today" by
 * comparing each run's started_at against this floor.
 */
export function startOfDayUtcIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString();
}
